import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './MonitorAdd.scss';

import { AxiosError } from 'axios';
import _ from 'lodash';
import {
  Button,
  Icons,
  InputField,
  notify,
  PendingContent,
  Spinner,
  Switch,
  Tooltip,
} from 'plume-ui';

import { invalidateFetchCache } from 'plume-web-utils';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import uuid from 'react-uuid';
import { useRecoilValue } from 'recoil';

import { UseFetchCacheKeys } from '../../../cacheConfig';
import Feedback from '../../../components/Feedback/Feedback';
import '../../../components/Feedback/Feedback.scss';
import { Routes } from '../../../config';
import { DependencyContainer } from '../../../DependencyContainer';
import { useFeaturePermission } from '../../../hooks/useFeaturePermission';
import { useFetchData } from '../../../hooks/useFetchData';
import { MixPanelEvents } from '../../../mixPanelEvents';
import { activePartnerAtom } from '../../../state';
import { CsvFileSummary, Maybe } from '../../../types';
import { Permission } from '../../../userPermissionConfig';
import FormattedMessage from '../../../utils/components/FormattedMessage';
import { invalidateQueries } from '../../../utils/graphqlConfig';
import { AdjustableComponent } from '../../../utils/hooks/useClasses';
import { useRedirectToRoute } from '../../../utils/hooks/useRedirectToRoute';
import { useTrackEvent } from '../../trackingAnalytics/hooks/useTrackEvent';
import { AvailableScreens } from '../../trackingAnalytics/types';
import {
  defaultMonitorApiPayload,
  IncludeExclude,
} from '../components/AddMonitor/fixtures';
import {
  finalizeMonitorAPIPayload,
  getOperatorFromSequenceString,
  includeExcludeSequenceMarker,
  isLocationFilterEmpty,
  monitorAPIPayloadApplyCriteria,
  monitorContainsIncludeExclude,
  parseMonitorToAPIPayload,
} from '../components/AddMonitor/helpers';
import {
  criteriasToIssueGroups,
  signalIssuesToCriteriaDisplay,
} from '../components/AddMonitor/IssueFilterConfig/helpers';
import IssueFilterConfig from '../components/AddMonitor/IssueFilterConfig/IssueFilterConfig';
import { IssueFilterGroup } from '../components/AddMonitor/IssueFilterConfig/types';
import {
  mapLocationAttrsToLocationCategories,
  stripLocationFilters,
} from '../components/AddMonitor/LocationFilterConfig/helpers';
import LocationFilterConfig from '../components/AddMonitor/LocationFilterConfig/LocationFilterConfig';
import { LocationFilterConfigPayload } from '../components/AddMonitor/LocationFilterConfig/types';
import MonitorFinishModal from '../components/AddMonitor/renderers/MonitorFinishModal';
import {
  LocationAttributes,
  LocationFilterType,
  MonitorApiPayload,
  Operator,
} from '../components/AddMonitor/types';
import useMonitorMetadata from '../hooks/useMonitorMetadata';
import { FilterData, Monitor, MonitorsTab } from '../types';

type MonitorAddProps = {
  test?: number | 200;
};

type MonitorAddStyles = {
  root: string;
};

const { monitorsService } = new DependencyContainer();

const MonitorAdd: AdjustableComponent<
  MonitorAddProps,
  MonitorAddStyles
> = () => {
  const { t } = useTranslation();
  const [finishButtonDisabled, setFinishButtonDisabled] = useState<boolean>(
    false,
  );
  const [loadingCreate, setLoadingCreate] = useState<boolean>(false);
  const activePartner = useRecoilValue(activePartnerAtom);
  const redirectToRoute = useRedirectToRoute();
  const trackEvent = useTrackEvent();

  const { monitorId } = useParams<{ monitorId: string }>();
  const { hasPermission } = useFeaturePermission();

  const createdMonitorId = useRef<string>();
  const [showSaveModal, setShowSaveModal] = useState<boolean>(false);

  const [incExcList, setIncExcList] = useState<Maybe<CsvFileSummary>>();
  const [currentCriteria, setCurrentCriteria] = useState<IssueFilterGroup[]>(
    [],
  );
  const [monitorApiPayload, setMonitorApiPayload] = useState<MonitorApiPayload>(
    defaultMonitorApiPayload,
  );

  const history = useHistory();

  const {
    monitorMetadata,
    loading: metadataLoading,
    error: metadataError,
  } = useMonitorMetadata();

  const notAvailable = t('monitorAdd.locationsCount.notAvailable');
  const updating = t('monitorAdd.locationsCount.updating');

  const [locationsCount, setLocationsCount] = useState<number | string>(
    notAvailable,
  );

  const fetchHarvestData = async (
    partnerId: string,
    locationAttributes: LocationAttributes,
  ): Promise<FilterData> => {
    return monitorsService.getDynamicFilterData(partnerId, locationAttributes);
  };

  // Workaround to ignore state updates from previous requests
  // to fetch locations count - so we don't send AbortController many levels deep
  const updateLocationsReqId = useRef('');

  const fetchAndUpdateLocationsCount = useCallback(
    async (dataToSend: LocationAttributes) => {
      if (!activePartner?.id) {
        return;
      }

      dataToSend.filters = dataToSend.filters.filter(
        (filt) => !isLocationFilterEmpty(filt),
      );

      updateLocationsReqId.current = uuid();
      const currId = updateLocationsReqId.current;

      setLocationsCount(updating);
      try {
        const harvestData = await fetchHarvestData(
          activePartner.id,
          dataToSend,
        );
        if (currId !== updateLocationsReqId.current) return;
        setLocationsCount(harvestData?.rowCount?.toLocaleString('en-US'));
      } catch (e) {
        if (currId !== updateLocationsReqId.current) return;
        setLocationsCount('ERROR'); // TODO check if we make this visible to the user
      }
    },
    [activePartner],
  );

  const debouncedFetchAndUpdateLocationsCount = useCallback(
    _.debounce(
      async (data: LocationAttributes) => fetchAndUpdateLocationsCount(data),
      500,
    ),
    [fetchAndUpdateLocationsCount],
  );

  const [monitorLoading, monitor] = useFetchData<Monitor, any>(
    async () => {
      if (!activePartner?.id) return;

      if (!monitorId) {
        fetchAndUpdateLocationsCount({
          filters: [],
          operator: Operator.OR,
        });
        return;
      }

      const res = await monitorsService.getMonitorId(
        activePartner.id,
        monitorId,
      );

      setMonitorApiPayload(parseMonitorToAPIPayload(res));

      if (!monitorContainsIncludeExclude(res)) {
        fetchAndUpdateLocationsCount(
          res.locationAttributes || { filters: [], operator: Operator.OR },
        );
      } else {
        setLocationsCount(notAvailable);
      }

      return res;
    },
    undefined,
    [activePartner, monitorId, monitorMetadata], // Monitor data redundant? - extra rerenders / api calls
    () => monitorMetadata === undefined,
  );

  useEffect(() => {
    if (monitorMetadata === undefined) {
      if (metadataError) {
        notify({
          title: t('error'),
          body: 'Failed to fetch metadata. Please try again.',
          type: 'error',
        });
      }
      return;
    }
  }, [monitorMetadata, metadataError]);

  useEffect(() => {
    setFinishButtonDisabled(
      !hasPermission(Permission.AddMonitor) ||
        !monitorApiPayload.name?.length ||
        !Object.keys(monitorApiPayload.criteria!.criterias).length ||
        Boolean(
          monitorApiPayload.locationAttributes &&
            monitorApiPayload.locationAttributes.filters.some(
              isLocationFilterEmpty,
            ),
        ),
    );
  }, [monitorApiPayload]);

  useEffect(() => {
    window.scrollTo(0, 0);

    trackEvent({
      eventName: MixPanelEvents.SCREEN,
      additionalContent: {
        SCREEN: monitorId
          ? AvailableScreens.MonitorEdit
          : AvailableScreens.MonitorAdd,
      },
    });
  }, []);

  useEffect(() => {
    return history.listen(() => {
      if (history.action === 'POP' || history.action === 'PUSH') {
        clearApiPayload();
      }
    });
  });

  const handleBackClick = () => {
    clearApiPayload();
    redirectToRoute(Routes.MonitorsTab, {
      partnerId: activePartner?.id,
      tab: MonitorsTab.User,
    });
  };

  const postCallback = (isOutreach: boolean) => {
    setShowSaveModal(false);
    if (isOutreach) {
      clearApiPayload();
      redirectToRoute(Routes.OutreachEdit, {
        partnerId: activePartner?.id,
        monitorId: createdMonitorId.current || monitorId,
      });
    } else {
      handleBackClick();
    }
  };

  const clearApiPayload = () => setMonitorApiPayload(defaultMonitorApiPayload);

  const handleFinishError = (e: AxiosError) => {
    switch (e.code) {
      case 'ETIMEDOUT':
        notify({
          title: t('warning'),
          body: t('monitorAdd.timeoutWarning'),
          type: 'alert',
          mustDismiss: true,
        });
        break;
      default:
        notify({
          title: t('error'),
          body: t('genericErrorMessage'),
          type: 'error',
        });
    }
    setLoadingCreate(false);
    setFinishButtonDisabled(false);
  };

  const handleFinishClick = () => {
    setFinishButtonDisabled(true);
    setLoadingCreate(true);
    if (monitorId) {
      monitorsService
        .patchMonitor(
          activePartner?.id || '',
          monitorId,
          finalizeMonitorAPIPayload(monitorApiPayload, incExcList),
        )
        .then(async () => {
          // Invalidate cache for user monitor list so that the list is updated
          invalidateFetchCache(UseFetchCacheKeys.UserMonitorList);
          invalidateQueries([
            {
              query: 'signal_3_p_evaluate_monitor_hourly_freq',
              invalidateIfContains: `${monitorId}`,
            },
            {
              query: 'signal_3_p_evaluate_monitor_freq_aggregate',
              invalidateIfContains: `${monitorId}`,
            },
          ]);
          setFinishButtonDisabled(false);
          setLoadingCreate(false);
          clearApiPayload();
          redirectToRoute(Routes.MonitorsTab, {
            partnerId: activePartner?.id,
            tab: MonitorsTab.User,
          });
        })
        .catch(handleFinishError);
    } else {
      monitorsService
        .createMonitor(
          activePartner?.id || '',
          finalizeMonitorAPIPayload(monitorApiPayload, incExcList),
        )
        .then((monitor) => {
          invalidateFetchCache(UseFetchCacheKeys.UserMonitorList);
          setFinishButtonDisabled(false);
          setLoadingCreate(false);
          createdMonitorId.current = monitor.monitorId;
          setShowSaveModal(true);
        })
        .catch(handleFinishError);
    }
  };

  const handleCriteriaChange = (criteria: IssueFilterGroup[]) => {
    setCurrentCriteria(criteria);
    setMonitorApiPayload((currPayload) =>
      monitorAPIPayloadApplyCriteria(criteria, currPayload),
    );
  };

  const handleLocationFilterChange = (data: LocationFilterConfigPayload) => {
    if (data.isIncludeExclude) {
      setLocationsCount(notAvailable);
      setIncExcList(data.list);
      setMonitorApiPayload((currPayload) => ({
        ...currPayload,
        locationAttributes: undefined,
      }));
    } else {
      setIncExcList(undefined);
      const { data: newValues } = data;
      setMonitorApiPayload((currPayload) => {
        const newPayload = monitorAPIPayloadApplyCriteria(currentCriteria, {
          ...currPayload,
          locationAttributes: {
            operator: currPayload.locationAttributes?.operator ?? Operator.AND,
            filters: stripLocationFilters(newValues),
          },
        });
        if (newPayload.locationAttributes) {
          debouncedFetchAndUpdateLocationsCount(newPayload.locationAttributes);
        }
        return newPayload;
      });
    }
  };

  const handleLocationsOperatorChange = (operator: Operator) => {
    setMonitorApiPayload((currPayload) => {
      const newPayload = {
        ...currPayload,
        locationAttributes: {
          filters: currPayload.locationAttributes?.filters ?? [],
          operator,
        },
      };
      if (newPayload.locationAttributes) {
        debouncedFetchAndUpdateLocationsCount(newPayload.locationAttributes);
      }
      return newPayload;
    });
  };

  const locationFiltersInitial = useMemo(() => {
    if (!monitor) return undefined;

    if (
      monitor.criteria &&
      monitor.criteria.criterias[includeExcludeSequenceMarker]
    ) {
      return {
        data: String(
          monitor.criteria.criterias[includeExcludeSequenceMarker]
            .selectedValue,
        ),
        listType: (monitor.criteria.criterias[includeExcludeSequenceMarker]
          .criteriaType === 'excludeList'
          ? 'exclude'
          : 'include') as IncludeExclude,
        isIncludeExclude: true as const,
      };
    }

    if (!monitor.locationAttributes) return undefined;

    return {
      isIncludeExclude: false as const,
      data:
        monitor.locationAttributes &&
        monitor.locationAttributes.filters
          .map((item) => {
            const metaItem = (
              monitorMetadata?.locationAttributes.metadata ?? []
            ).find((metaItem) => metaItem.columnName === item.columnName);
            if (metaItem)
              return {
                ...item,
                ...metaItem,
                columnValue:
                  item.columnValue === 'True' || item.columnValue === 'False' // temp hack for "True"|"False" -> true|false
                    ? item.columnValue === 'True'
                    : item.columnValue,
              };
            else return undefined;
          })
          .filter((item): item is LocationFilterType => !!item),
    };
  }, [monitor, monitorMetadata]);

  const issueFiltersInitial = useMemo(() => {
    if (!monitor || !monitorMetadata) return undefined;
    if (!monitor.criteria) return undefined;
    const data = criteriasToIssueGroups(
      monitor.criteria,
      monitorMetadata.signalIssues,
    );

    setCurrentCriteria(data);
    return data;
  }, [monitor, monitorMetadata]);

  return (
    <div className="MonitorAdd">
      <div className="MonitorAdd__topBar">
        <Button styleVariant="tertiary-grey" onClick={handleBackClick}>
          <Icons.ArrowLeftIcon width={24} /> <FormattedMessage id="back" />
        </Button>
        <FormattedMessage
          id={monitorId ? 'monitorAdd.editMonitor' : 'monitorAdd.addMonitor'}
        />
        <Button
          loading={loadingCreate}
          onClick={handleFinishClick}
          disabled={finishButtonDisabled}
          styleVariant="superprimary"
        >
          {t('confirmation.finish')}
        </Button>
      </div>
      <div className="MonitorAdd__topBar__spacing" />
      <div className="MonitorAdd__grid">
        <div className="MonitorAdd__gridItem">
          <div className="MonitorAdd__header">
            <div className="MonitorAdd__nameInput">
              <InputField
                label={t('monitorAdd.monitorName')}
                value={monitorApiPayload.name}
                required
                name="monitorName"
                onChange={(event) => {
                  const val = (event.target as HTMLInputElement).value;
                  setMonitorApiPayload((payload) => ({
                    ...payload,
                    name: val,
                  }));
                }}
              />
            </div>

            <div className="MonitorAdd__switchWithTooltip">
              <span
                className={`MonitorAdd__switch ${
                  monitorApiPayload.harvestSyncEnabled ? 'selected_style' : ''
                }`}
              >
                <Switch
                  title={t('monitorAdd.switch.syncAsSegment')}
                  selected={Boolean(monitorApiPayload.harvestSyncEnabled)}
                  onToggle={() =>
                    setMonitorApiPayload({
                      ...monitorApiPayload,
                      harvestSyncEnabled: !monitorApiPayload.harvestSyncEnabled,
                    })
                  }
                  classes={(current) => {
                    return {
                      ...current,
                      switchTitle: monitorApiPayload.harvestSyncEnabled
                        ? `${current.switchTitle}`
                        : `${current.switchTitle} Switch--title__disabled`,
                    };
                  }}
                />
              </span>
              <span className="MonitorAdd__tooltip">
                <Tooltip
                  position="below"
                  alignment="right"
                  label={t('monitorAdd.switch.tooltip')}
                  overrideMaxCharacters
                >
                  <Icons.InfoIcon width={16} />
                </Tooltip>
              </span>
            </div>
          </div>
        </div>

        <div className="MonitorAdd__gridItem">
          <div className="MonitorAdd__filter MonitorAdd__container">
            <div className="MonitorAdd__filter__intro">
              <div className="MonitorAdd__filter__intro__name">
                <div className="MonitorAdd__filter__intro__name__title">
                  <FormattedMessage id="monitorAdd.filterLocations.title" />
                </div>
                <div className="MonitorAdd__filter__intro__name__subtitle">
                  <FormattedMessage id="monitorAdd.filterLocations.subtitle" />
                </div>
              </div>
              <div className="MonitorAdd__filter__intro__locations">
                <div className="MonitorAdd__filter__intro__locations__title">
                  <FormattedMessage id="monitorAdd.locationsCount.title" />
                  <Tooltip
                    label={t('monitorAdd.locationsCount.tooltip')}
                    position="below"
                    alignment="right"
                    overrideMaxCharacters
                    openInPortal
                  >
                    <Icons.InfoIcon width={16} />
                  </Tooltip>
                </div>
                <div className="MonitorAdd__filter__intro__locations__number">
                  {locationsCount}
                </div>
              </div>
            </div>
            <div className="MonitorAdd__filter__body">
              <PendingContent
                loading={metadataLoading || monitorLoading}
                loader={Spinner}
              >
                {metadataError ? (
                  <FormattedMessage id="somethingWentWrong" />
                ) : (
                  <LocationFilterConfig
                    filterCategories={mapLocationAttrsToLocationCategories(
                      monitorMetadata?.locationAttributes.metadata ?? [],
                    )}
                    initialData={locationFiltersInitial}
                    onChange={handleLocationFilterChange}
                    onChangeOperator={handleLocationsOperatorChange}
                    initialOperator={getOperatorFromSequenceString(
                      monitor?.locationAttributes?.operator,
                    )}
                  />
                )}
              </PendingContent>
            </div>
          </div>
        </div>

        <div className="MonitorAdd__gridItem">
          <div className="MonitorAdd__filter MonitorAdd__container">
            <div className="MonitorAdd__filter__intro">
              <div className="MonitorAdd__filter__intro__name">
                <div className="MonitorAdd__filter__intro__name__title">
                  <FormattedMessage id="monitorAdd.monitorEvents.title" />
                </div>
                <div className="MonitorAdd__filter__intro__name__subtitle">
                  <FormattedMessage id="monitorAdd.monitorEvents.subtitle" />
                </div>
              </div>
            </div>
            <PendingContent
              loading={metadataLoading || monitorLoading}
              loader={Spinner}
            >
              {monitorMetadata && (
                <div className="MonitorAdd__filter__body">
                  <IssueFilterConfig
                    items={signalIssuesToCriteriaDisplay(
                      monitorMetadata.signalIssues,
                    )}
                    initialData={issueFiltersInitial}
                    onChange={(data) => handleCriteriaChange(data)}
                  />
                </div>
              )}
              <Feedback
                screen={
                  monitorId
                    ? AvailableScreens.MonitorEdit
                    : AvailableScreens.MonitorAdd
                }
              />
            </PendingContent>
          </div>
        </div>
      </div>

      <MonitorFinishModal
        name={monitorApiPayload.name!}
        show={showSaveModal}
        callback={(isOutreach: boolean) => postCallback(isOutreach)}
      />
    </div>
  );
};

export default memo(MonitorAdd);
